import { customerQuery } from "@common/api/customers"
import { Badge } from "@common/components/ui/badge"
import { useCurrentUser } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { FilterBar } from "@/components/layout/filter-bar"
import { requirePermission } from "@/lib/require-permission"
import { CustomerSummary } from "./-components/customer-summary"
import { CustomerTabs } from "./-components/customer-tabs"
import { tabsFor } from "./-lib/tabs"

export const Route = createFileRoute("/_layout/customer_/$customerId")({
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "customer:read", "/"),
  loader: async ({ context: { queryClient }, params: { customerId } }) => {
    const customer = await queryClient.ensureQueryData(
      customerQuery({ customer_id: customerId }),
    )
    return {
      breadcrumb: [
        { label: "Customers", href: "/customer" },
        {
          label: customer.name || "Unnamed customer",
          href: "/customer/$customerId",
        },
      ],
    }
  },
  component: CustomerPage,
})

function CustomerPage(): React.ReactElement {
  const { customerId } = Route.useParams()
  const { data: customer } = useSuspenseQuery(
    customerQuery({ customer_id: customerId }),
  )
  const tabs = tabsFor(useCurrentUser())

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <FilterBar>
        <div className="flex h-8 items-center gap-3">
          <CustomerSummary customer={customer} />
          {customer.archived_at && <Badge variant="secondary">Archived</Badge>}
        </div>
        <div className="mt-1 flex h-8 items-center gap-2">
          <CustomerTabs customerId={customer.id} tabs={tabs} />
        </div>
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
